import React, { useEffect } from 'react';
import { Container, Row, Col, Button } from 'reactstrap';
import { Link } from 'react-router-dom';

import Layout from '../../Layout/Layout';
import {
  withLoading,
  useTranslator,
  HashimotoTestEdge,
  useAppContext,
} from '@front-activerse/common-components';

import TitleBar from '../../Layout/TitleBar';
import UniversalPlaceholder from '../../Layout/UniversalPlaceholder';
import TestsResult from './TestResult';
import createMeasurements from '../Helpers/createMeasurements';
import { HashimotoTestMeasurementFieldEnum, TestMeasurement } from '../types';

interface ILabTestsContainer {
  hashimotoTestHistory: HashimotoTestEdge[];
  mobileAppMode: boolean;
  removeTest: any;
}

export const LabTestsContainer: React.FC<ILabTestsContainer> = ({
  hashimotoTestHistory,
  removeTest,
}) => {
  const { t } = useTranslator();

  const {
    images: { addLabTestIcon, noMeasurementsIcon },
  } = useAppContext();

  useEffect(() => {
    // trackFirebaseEvent('screen_view', {
    //   firebase_screen: 'Lab_results',
    // });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const tshMeasurements: TestMeasurement[] = createMeasurements(
    hashimotoTestHistory,
    HashimotoTestMeasurementFieldEnum.TSH
  );
  const ft3Measurements: TestMeasurement[] = createMeasurements(
    hashimotoTestHistory,
    HashimotoTestMeasurementFieldEnum.FT3
  );
  const ft4Measurements: TestMeasurement[] = createMeasurements(
    hashimotoTestHistory,
    HashimotoTestMeasurementFieldEnum.FT4
  );
  const antiTpoMeasurements: TestMeasurement[] = createMeasurements(
    hashimotoTestHistory,
    HashimotoTestMeasurementFieldEnum.ANTI_TPO
  );
  const antiTgMeasurements: TestMeasurement[] = createMeasurements(
    hashimotoTestHistory,
    HashimotoTestMeasurementFieldEnum.ANTI_TG
  );

  return (
    <Layout>
      <div className="page-template-lab-tests full-height2">
        <TitleBar showBackBtn={false}>{t('labTests.navbarTitle')}</TitleBar>
        <Container className="py-md-4">
          <Row>
            <Col md={{ size: 10, offset: 1 }}>
              <div className="lab-tests-container">
                <div className="white-box mb-xxl">
                  <div className="d-md-flex align-items-center justify-content-between">
                    <div className="d-flex align-items-center">
                      <img src={addLabTestIcon} className="me-2 " alt="" />
                      <p className="small text-text-main-color mb-0">
                        {t('addLabTests.notification.content')}
                      </p>
                    </div>
                    <div className="text-center mt-3 mt-md-0">
                      <Button color="primary" tag={Link} to="/lab-tests/add" size="sm">
                        {t('addLabTests.notification.button')}
                      </Button>
                    </div>
                  </div>
                </div>

                {hashimotoTestHistory.length > 0 && (
                  <Row>
                    <Col md={6}>
                      <h2 className="title-medium semi-bold">
                        {t('labTests.recentResults.title')}
                        <Button
                          color="link"
                          size="sm"
                          tag={Link}
                          to="/lab-tests/history/tsh"
                          className="float-end px-0 py-0"
                        >
                          {t('labTests.recentResults.historyButton')}
                        </Button>
                      </h2>

                      <div className="white-box test-results">
                        <TestsResult
                          measurements={tshMeasurements}
                          title="tsh"
                          unit="mIU/l"
                          removeTest={removeTest}
                        />
                        <hr className="stroke-light" />
                        <TestsResult
                          measurements={ft3Measurements}
                          title="ft3"
                          unit="pmol/l"
                          removeTest={removeTest}
                        />
                        <hr className="stroke-light" />
                        <TestsResult
                          measurements={ft4Measurements}
                          title="ft4"
                          unit="pmol/l"
                          removeTest={removeTest}
                        />
                        <hr className="stroke-light" />
                        <TestsResult
                          measurements={antiTgMeasurements}
                          title="antiTg"
                          unit="IU/ml"
                          removeTest={removeTest}
                        />
                        <hr className="stroke-light" />
                        <TestsResult
                          measurements={antiTpoMeasurements}
                          title="antiTpo"
                          unit="IU/ml"
                          removeTest={removeTest}
                        />
                      </div>
                    </Col>
                    <Col md={6}>
                      <h2 className="title-medium semi-bold">
                        {t('labTests.referenceValues.title')}
                      </h2>

                      <div className="white-box reference-values">
                        <p className="m-0">
                          <span className="description">{t('labTests.tsh')}</span>
                          <span className="float-end">
                            <strong className="text-text-main-color semi-bold">0,4 - 4,0</strong>{' '}
                            <span className="description">{t('labTests.tsh.unit')}</span>
                          </span>
                        </p>
                        <hr className="stroke-light my-3" />
                        <p className="m-0">
                          <span className="description">{t('labTests.ft3')}</span>
                          <span className="float-end">
                            <strong className="text-text-main-color semi-bold">2,25 - 6,0</strong>{' '}
                            <span className="small">{t('labTests.ft3.unit')}</span>
                          </span>
                        </p>
                        <hr className="stroke-light my-3" />
                        <p className="m-0">
                          <span className="description">{t('labTests.ft4')}</span>
                          <span className="float-end">
                            <strong className="text-text-main-color semi-bold">10,0 - 35,0</strong>{' '}
                            <span className="small">{t('labTests.ft4.unit')}</span>
                          </span>
                        </p>
                        <hr className="stroke-light my-3" />
                        <p className="m-0">
                          <span className="description">{t('labTests.antiTg')}</span>
                          <span className="float-end">
                            <strong className="text-text-main-color semi-bold">&lt; 115</strong>{' '}
                            <span className="small">{t('labTests.antiTg.unit')}</span>
                          </span>
                        </p>
                        <hr className="stroke-light my-3" />
                        <p className="m-0">
                          <span className="description">{t('labTests.antiTpo')}</span>
                          <span className="float-end">
                            <strong className="text-text-main-color semi-bold">&lt; 34</strong>{' '}
                            <span className="small">{t('labTests.antiTpo.unit')}</span>
                          </span>
                        </p>
                        <hr />
                        <p className="description mb-0">{t('labTests.referenceRangeDisclaimer')}</p>
                      </div>
                    </Col>
                  </Row>
                )}

                {hashimotoTestHistory.length === 0 && (
                  <div className="text-center">
                    <img src={noMeasurementsIcon} className="mb-m" alt="" />
                    <p className="text-text-variant-color">{t('labTests.noTestsContent')}</p>
                  </div>
                )}
              </div>
            </Col>
          </Row>
        </Container>
      </div>
    </Layout>
  );
};

const Placeholder = () => <UniversalPlaceholder titleBar="labTests.navbarTitle" />;

export default withLoading(LabTestsContainer, Placeholder);
